/**
 * BL-product-grouping.js
 * AJAX-based navigation for product grouping swatches.
 * Replaces full-page reload with a section-only fetch + DOM swap.
 *
 * Selector contract: targets [data-bl-grouping-root] exclusively.
 * The visual root is .bl-grouping-base; washbasin-v2 may still carry the
 * legacy .bl-product-configurator class.
 */
(function () {
  // 2026-10-15: UNA instancia por pagina. El <script> vive dentro de la seccion main y cada swap lo
  // recreaba: nacia otra instancia con su sectionCache vacia y su eagerPrefetch volvia a pedir todos
  // los hermanos (Ronda: 16 peticiones por salto en vez de las 2-4 URLs nuevas, el resto 304). Si el
  // script vuelve a ejecutarse (swap del tema, editor, bloque repetido), la instancia viva engancha
  // el root actual y esta copia no hace nada mas.
  if (typeof window.__blGroupingInit === 'function') {
    window.__blGroupingInit();
    return;
  }

  let abortController = null;
  const sectionCache = new Map();
  const pendingPrefetches = new Map();
  let persistentWashbasinBlockState = null;
  let persistentMediaGalleryState = null;
  let persistentTolstoyBlockState = null;
  const CACHE_TTL = 5 * 60 * 1000;
  // 2026-10-15: 20 → 40 y LRU (cada acierto pasa al final). La precarga de UN producto ya mete hasta
  // 12 colores + 6 medidas + 4 de la segunda tanda: con 20, al saltar a otro producto (Konfiguration,
  // medida ajena) se expulsaban los hermanos del anterior y volver los pedia otra vez (Ronda: 13-14
  // peticiones `?sections=` de ~31 KB por vuelta). 40 cubre ir y volver entre dos productos. Cada
  // entrada son ~340.000 caracteres (la seccion main lleva «€», «–»: cadena de 2 bytes, ~0,6 MB).
  const CACHE_MAX = 40;
  const EAGER_PREFETCH_LIMIT = 12;
  const EAGER_SIZE_PREFETCH_LIMIT = 6;   // V4: medidas propias del producto (fila de medida)
  // 2026-10-15: segunda tanda de la precarga ansiosa (ver eagerPrefetchExtra): filas que la primera no
  // alcanza (Ronda: Griff y Konfiguration, detras de 15 colores), 1 foto por hermano.
  const EAGER_EXTRA_PREFETCH_LIMIT = 4;
  const EAGER_EXTRA_IMG_COUNT = 1;
  // URL a la que lleva el boton Atras tras un salto por clic (la pagina que se acaba de dejar). Si fue
  // la de entrada no esta en cache: la segunda tanda la pide. null tras un popstate.
  let backUrl = null;
  // Tracks image keys already queued for preload so we never insert duplicate
  // <link rel="preload"> tags across multiple prefetch calls.
  const _preloadedImgKeys = new Set();
  // 2026-10-08 (web vitals): cuantas fotos de cada hermano se precargan y con que prioridad. La
  // precarga ANSIOSA (todos los colores al cargar la ficha) va a prioridad baja y solo con la foto
  // que se ve (1 en el slider movil, 2 en la rejilla de escritorio); el hover/touch precarga 3 a
  // prioridad normal porque ahi el toque es inminente. Antes eran 36 fotos (~1,9 MB) a prioridad
  // normal compitiendo con la foto principal.
  // 2026-10-10 (QA): con 1 foto por hermano en movil, las fotos 3-4 del hermano elegido tardaban
  // medio segundo tras el swap. Vuelven a 4 por hermano (prioridad baja y solo tras la foto
  // principal), y el hover/touch completa hasta 4 a prioridad normal.
  const EAGER_IMG_COUNT = 4;
  const HOVER_IMG_COUNT = 4;
  // Por URL de seccion, cuantas fotos se han mandado ya a precargar: evita volver a parsear con
  // DOMParser la seccion cacheada en cada swap (eran 12 parses de ~30 KB dentro de cada clic).
  const _preloadedCountByUrl = new Map();

  // 2026-10-08 (INP): el clic solo PINTA la opcion elegida; el swap de la seccion (parsear, insertar
  // ~2.000 nodos, Swiper, selectores...) corre en la tarea siguiente, despues de pintar. En campo el
  // toque bloqueaba 0,5-1,2 s en Android porque todo eso iba dentro del propio clic: la seccion
  // precargada se resolvia en una microtarea, antes del pintado.
  // Doble rAF: el segundo solo corre en el frame SIGUIENTE, asi que el primero (con la opcion ya
  // marcada) se ha presentado seguro. Con rAF + setTimeout(0) el temporizador se colaba a veces
  // antes de presentar el frame y el toque volvia a durar lo que el swap.
  function afterNextPaint(fn) {
    let done = false;
    const run = () => { if (done) return; done = true; fn(); };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(run));
    setTimeout(run, 250);   // rAF no corre con la pestana oculta: respaldo
  }

  // El root puede haber sido reemplazado por un swap anterior (viewTransition deja el viejo 500 ms
  // oculto en el DOM, detras del nuevo): se trabaja siempre con el que esta vivo.
  function liveWrapper(wrapper) {
    if (wrapper && wrapper.isConnected) {
      const section = wrapper.closest('.shopify-section');
      if (!section || section.style.display !== 'none') return wrapper;
    }
    return document.querySelector('[data-bl-grouping-root]') || wrapper;
  }

  function init() {
    const wrapper = document.querySelector('[data-bl-grouping-root]');
    if (!wrapper) return;

    loadGroupingMatrix(wrapper);
    bindHoverHandlers(wrapper);
    bindClickHandler(wrapper);
    bindPopstate();
    eagerPrefetch(wrapper);
  }

  function getSectionIdFromNode(node) {
    if (!node || !node.id) return null;
    return node.id.replace(/^shopify-section-/, '');
  }

  // 2026-10-15 (BL-121): secciones de la plantilla, aparte de la principal y la barra fija, que dependen
  // del producto y se piden y sustituyen en cada salto del grouping. Se marcan con la clase de envoltorio
  // `bl-grouping-refresh` ("class" del schema: va en el propio .shopify-section, asi que sigue ahi aunque
  // la seccion se pinte vacia). Hoy: Descripcion avanzada (descripcion + pestanas de metafields) y
  // Descargas. El selector por id de Descargas es el contrato anterior y se queda como respaldo.
  // Section Rendering API: maximo 5 secciones por peticion (principal + barra fija + estas).
  const REFRESH_SECTION_SELECTOR = '.shopify-section.bl-grouping-refresh, .shopify-section[id*="__bl_downloads"]';

  function getRefreshSectionNodes() {
    const activeSectionsById = new Map();

    Array.from(document.querySelectorAll(REFRESH_SECTION_SELECTOR)).forEach(node => {
      const sectionId = getSectionIdFromNode(node);
      if (!node.isConnected || !sectionId) return;

      // HTMLUpdateUtility.viewTransition keeps the outgoing node in the DOM
      // hidden for 500ms. Ignore those transition leftovers so rapid grouping
      // swaps cannot replace stale nodes and multiply the section.
      if (node.style.display === 'none') return;

      if (activeSectionsById.has(sectionId)) {
        node.remove();
        return;
      }

      activeSectionsById.set(sectionId, node);
    });

    return Array.from(activeSectionsById.values());
  }

  // Acordeones abiertos de una seccion antes del swap, para abrir los mismos en la nueva (misma
  // plantilla = mismas pestanas). Clave: el id del <details> si lo tiene (Descargas), si no el titulo
  // de la pestana con su numero de aparicion (Descripcion avanzada no pone id).
  function getAccordionKeys(sectionNode) {
    const seen = new Map();
    return Array.from(sectionNode.querySelectorAll('details.accordion-details')).map(details => {
      if (details.id) return { details, key: `#${details.id}` };
      const title = (details.querySelector('summary')?.textContent || '').replace(/\s+/g, ' ').trim();
      const count = seen.get(title) || 0;
      seen.set(title, count + 1);
      return { details, key: `${title}|${count}` };
    });
  }

  function captureAccordionState(sectionNode) {
    if (!sectionNode) return null;
    const state = new Map();
    getAccordionKeys(sectionNode).forEach(({ details, key }) => state.set(key, details.hasAttribute('open')));
    return state.size ? state : null;
  }

  // Se aplica a la seccion entrante ANTES de insertarla: AccordionDetails lee `open` en su constructor,
  // que corre al conectarse, asi que queda abierta sin animacion (como un "open by default").
  function applyAccordionState(sectionNode, state) {
    if (!sectionNode || !state) return;
    getAccordionKeys(sectionNode).forEach(({ details, key }) => {
      if (!state.has(key)) return;
      const isOpen = state.get(key);
      details.toggleAttribute('open', isOpen);
      details.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function getUniqueSectionIds(targets) {
    const sectionTargets = [
      targets.main,
      targets.sticky,
      ...(targets.refresh || [])
    ];

    return sectionTargets
      .map(target => target?.id)
      .filter((value, index, all) => value && all.indexOf(value) === index)
      .slice(0, 5);
  }

  function getSectionTargets(wrapper) {
    const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    const mainSectionNode =
      wrapper?.closest('.shopify-section')
      || productInfo?.closest('.shopify-section');

    const stickySectionNode = document.getElementById('shopify-section-sticky-atc-bar');
    const refreshSections = getRefreshSectionNodes()
      .map(node => ({
        node,
        id: getSectionIdFromNode(node)
      }))
      .filter(target => target.node && target.id);

    return {
      main: {
        node: mainSectionNode,
        id: getSectionIdFromNode(mainSectionNode)
      },
      sticky: {
        node: stickySectionNode,
        id: getSectionIdFromNode(stickySectionNode)
      },
      refresh: refreshSections
    };
  }

  function buildSectionRequestUrl(url, sectionIds) {
    const requestUrl = new URL(url, window.location.href);
    requestUrl.searchParams.set('sections', sectionIds.join(','));
    return requestUrl.toString();
  }

  function cloneSectionShell(node) {
    if (!node) return null;

    const shell = document.createElement(node.tagName.toLowerCase());
    Array.from(node.attributes).forEach(attribute => {
      shell.setAttribute(attribute.name, attribute.value);
    });

    return shell;
  }

  function parseSectionMarkup(markup, fallbackNode = null) {
    if (!markup) return null;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const wrappedSection = doc.querySelector('.shopify-section');
    if (wrappedSection) return wrappedSection;

    if (fallbackNode) {
      const shell = cloneSectionShell(fallbackNode);
      if (shell) {
        shell.innerHTML = markup;
        return shell;
      }
    }

    return doc.body.firstElementChild;
  }

  // 2026-10-10 (QA): la seccion llegaba como texto y se parseaba DOS veces dentro del clic (DOMParser
  // y luego outerHTML → innerHTML en HTMLUpdateUtility.viewTransition): ~120 ms a CPU x4. Ahora se
  // parsea UNA vez y, cuando la seccion vino precargada, en un hueco libre ANTES del clic; al pulsar se
  // adopta el nodo ya parseado. Los <script> se recrean para que se ejecuten, igual que hacia el tema.
  const PREPARSE_MAX = 8;
  const preparsedSections = new Map();   // requestUrl -> { mainId, node }

  function schedulePreparse(requestUrl, sectionMap, mainId, fallbackNode) {
    if (!sectionMap || !mainId || !sectionMap[mainId] || preparsedSections.has(requestUrl)) return;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    idle(() => {
      if (preparsedSections.has(requestUrl) || !getCached(requestUrl)) return;
      if (preparsedSections.size >= PREPARSE_MAX) {
        const oldest = preparsedSections.keys().next().value;
        if (oldest) preparsedSections.delete(oldest);
      }
      const node = parseSectionMarkup(sectionMap[mainId], fallbackNode);
      if (node) preparsedSections.set(requestUrl, { mainId, node });
    });
  }

  function takePreparsed(requestUrl, mainId) {
    const entry = preparsedSections.get(requestUrl);
    if (!entry) return null;
    preparsedSections.delete(requestUrl);
    return entry.mainId === mainId ? entry.node : null;
  }

  function adoptSectionNode(node) {
    const adopted = node.ownerDocument === document ? node : document.adoptNode(node);
    adopted.querySelectorAll('script').forEach((oldScript) => {
      // este mismo fichero no: navigateAjax ya llama a init() sobre el root nuevo (el nodo parseado queda inerte)
      if ((oldScript.getAttribute('src') || '').includes('BL-product-grouping.js')) return;
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
      script.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(script, oldScript);
    });
    return adopted;
  }

  // Mismo contrato que HTMLUpdateUtility.viewTransition (theme.js): la nueva se inserta delante, la
  // vieja se oculta y se retira a los 500 ms, ids de la vieja con sufijo. Sin reserializar.
  function viewTransitionAdopt(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    if (!oldNode || !newContent) return;
    preProcessCallbacks.forEach((callback) => callback(newContent));

    const newNode = adoptSectionNode(newContent);

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  function parseOptionalSectionMarkup(markup, fallbackNode = null) {
    if (markup == null || markup === '') {
      const shell = cloneSectionShell(fallbackNode);
      if (shell) shell.innerHTML = '';
      return shell;
    }

    return parseSectionMarkup(markup, fallbackNode) || cloneSectionShell(fallbackNode);
  }

  function getSelectedVariantFromSection(sectionNode) {
    const variantScript = sectionNode?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    if (!variantScript) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      return null;
    }
  }

  function updateDocumentTitle(sectionNode) {
    const titleNode = sectionNode?.querySelector('.product__block--title .product__title, .product__title');
    const nextTitle = titleNode?.textContent?.trim();
    if (!nextTitle) return;

    const currentTitle = document.title || '';
    const separators = [' | ', ' – ', ' - '];
    const separator = separators.find(item => currentTitle.includes(item));

    if (!separator) {
      document.title = nextTitle;
      return;
    }

    const suffix = currentTitle.split(separator).slice(1).join(separator).trim();
    document.title = suffix ? `${nextTitle}${separator}${suffix}` : nextTitle;
  }

  function applyLoadingState(node, isLoading) {
    if (!node) return;

    node.style.opacity = '';
    node.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function captureViewportPosition() {
    return {
      x: window.scrollX,
      y: window.scrollY
    };
  }

  function restoreViewportPosition(position) {
    if (!position) return;

    const restore = () => {
      if (window.scrollX !== position.x || window.scrollY !== position.y) {
        window.scrollTo(position.x, position.y);
      }
    };

    // 2026-10-10: NO quitar la llamada inmediata. Se probo dejar solo el rAF para ahorrar el layout
    // forzado (~30 ms a CPU x4) y el swap hacia saltar la pagina casi al pie (scrollY 465 → 2994) y
    // volver en el frame siguiente: un brinco visible en cada cambio de color o medida (QA 10-10).
    // El anclaje de scroll del navegador no cubre este swap (la seccion vieja pasa a display:none).
    restore();
    requestAnimationFrame(restore);
  }

  function getCached(key) {
    const entry = sectionCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.ts > CACHE_TTL) {
      sectionCache.delete(key);
      return null;
    }

    // LRU: lo que se vuelve a usar (la precarga del producto al que se vuelve) no es lo primero en salir
    sectionCache.delete(key);
    sectionCache.set(key, entry);
    return entry.data;
  }

  function setCached(key, data) {
    sectionCache.delete(key);
    if (sectionCache.size >= CACHE_MAX) {
      const oldestKey = sectionCache.keys().next().value;
      if (oldestKey) sectionCache.delete(oldestKey);
    }

    sectionCache.set(key, {
      data,
      ts: Date.now()
    });
  }

  function fetchSectionMap(requestUrl, options = {}) {
    const cached = getCached(requestUrl);
    if (cached) return Promise.resolve(cached);

    const pending = pendingPrefetches.get(requestUrl);
    if (pending) return pending;

    const fetchOptions = {};
    if (options.signal) fetchOptions.signal = options.signal;
    if (options.priority) fetchOptions.priority = options.priority;   // Chrome/Safari; el resto lo ignora

    const promise = fetch(requestUrl, fetchOptions)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(sectionMap => {
        setCached(requestUrl, sectionMap);
        return sectionMap;
      })
      .finally(() => {
        if (pendingPrefetches.get(requestUrl) === promise) {
          pendingPrefetches.delete(requestUrl);
        }
      });

    pendingPrefetches.set(requestUrl, promise);
    return promise;
  }

  function loadGroupingMatrix(wrapper) {
    const sectionNode = getSectionTargets(wrapper).main.node;
    const matrixScript = sectionNode?.querySelector('[id^="bl-grouping-matrix-"]') || document.querySelector('[id^="bl-grouping-matrix-"]');

    try {
      window.__blGroupingMatrix = JSON.parse(matrixScript?.textContent || '{}');
    } catch (error) {
      window.__blGroupingMatrix = {};
    }
  }

  function normalizeOptionText(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function normalizeSizeKey(value) {
    return normalizeOptionText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function extractNumericSizeToken(value) {
    return (value || '').toString().replace(/[^\d]/g, '');
  }

  function getComparableSizeValue(value) {
    return normalizeOptionText(value)
      .replace(/\s+/g, '')
      .replace(/[.,/x×-]/g, '')
      .replace(/"/g, '')
      .replace(/'/g, '')
      .replace(/inch|in|mm|cm|2s/g, '');
  }

  function looksLikeSizeValue(value) {
    const normalized = normalizeOptionText(value);
    const comparable = getComparableSizeValue(value);
    const numeric = extractNumericSizeToken(value);
    if (!normalized) return false;

    if (/^\d+([.,]\d+)?$/.test(normalized)) return true;
    if (/^\d+([.,]\d+)?\s*2s$/.test(normalized)) return true;
    if (/\d\s*(cm|mm)\b/.test(normalized)) return true;
    if (/^\d+([.,]\d+)?\s*(x|×|-|\/)\s*\d+([.,]\d+)?/.test(normalized)) return true;

    return comparable.length >= 2 && numeric.length >= 2 && !/[a-z]/.test(comparable);
  }

  function getSelectedValueFromOptionBlock(block) {
    if (!block) return '';

    const selectedSpan = block.querySelector('[data-selected-value]');
    if (selectedSpan?.textContent?.trim()) return selectedSpan.textContent.trim();

    const checked = block.querySelector('input[type="radio"]:checked');
    if (checked?.value?.trim()) return checked.value.trim();

    const select = block.querySelector('select');
    if (select?.value?.trim()) return select.value.trim();

    const active = block.querySelector('.is-active, .selected, [aria-checked="true"]');
    const activeValue = active?.dataset?.value || active?.dataset?.optionValue || active?.textContent;
    return activeValue?.trim() || '';
  }

  function getOptionValuesFromBlock(block) {
    if (!block) return [];

    const values = new Set();

    block.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (input.value?.trim()) values.add(input.value.trim());
    });

    block.querySelectorAll('select option').forEach((option) => {
      if (option.value?.trim()) values.add(option.value.trim());
      const optionText = option.textContent?.trim();
      if (optionText) values.add(optionText);
    });

    block.querySelectorAll('[data-value], [data-option-value]').forEach((node) => {
      const optionValue = node.dataset?.value || node.dataset?.optionValue || '';
      if (optionValue.trim()) values.add(optionValue.trim());
    });

    const selectedValue = getSelectedValueFromOptionBlock(block);
    if (selectedValue) values.add(selectedValue);

    return Array.from(values);
  }

  function blockLooksLikeSizeOption(block) {
    const values = getOptionValuesFromBlock(block).filter(Boolean);
    if (!values.length) return false;

    const sizeLikeValues = values.filter(looksLikeSizeValue);
    if (sizeLikeValues.length > 1) return true;
    if (sizeLikeValues.length > 0 && sizeLikeValues.length === values.length) return true;

    return looksLikeSizeValue(getSelectedValueFromOptionBlock(block));
  }

  function getStoredSelectedLabel(group, fallback = '') {
    if (!group) return fallback;

    const stored = group.dataset.blSelectedLabel;
    if (stored && stored.trim()) return stored.trim();

    const label = group.querySelector('[data-selected-value]')?.textContent?.trim();
    if (label) {
      group.dataset.blSelectedLabel = label;
      return label;
    }

    return fallback;
  }

  function getCurrentSelectedSize() {
    const picker = document.querySelector('variant-selects, variant-radios');

    if (picker) {
      const allBlocks = picker.querySelectorAll('fieldset, .product-form__input, .variant-picker__option');
      for (const block of allBlocks) {
        if (!blockLooksLikeSizeOption(block)) continue;

        const selectedValue = getSelectedValueFromOptionBlock(block);
        if (selectedValue) return selectedValue;
      }
    }

    const productSection = getSectionTargets(document.querySelector('[data-bl-grouping-root]')).main.node;
    const selectedVariant = getSelectedVariantFromSection(productSection);
    if (selectedVariant?.options?.length) {
      const candidate = selectedVariant.options.find(looksLikeSizeValue);
      if (candidate) return candidate;
    }

    return null;
  }

  // 2026-09-11 (lavabos Aris): firma completa de las opciones nativas elegidas, en orden de posicion
  // ("120|10-cm|ohne-hahnloch"), con la misma normalizacion que el handleize de Liquid. Solo tiene
  // sentido con mas de una opcion real; con una sola, la medida ya es la firma.
  function getCurrentSelectedSignature() {
    const picker = document.querySelector('variant-selects, variant-radios');
    if (!picker) return null;

    const blocks = Array.from(picker.querySelectorAll('[data-option-position]'));
    if (blocks.length < 2) return null;

    blocks.sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0));
    const values = blocks.map((block) => getSelectedValueFromOptionBlock(block));
    if (values.some((value) => !value)) return null;

    return values.map(normalizeSizeKey).join('|');
  }

  function resolveTargetVariantId(url) {
    const matrix = window.__blGroupingMatrix || {};
    const targetPath = new URL(url, window.location.href).pathname;
    const siblingMap = matrix[targetPath];
    if (!siblingMap) return null;

    // Primero la firma completa (conserva Hahnloch/Schürze al cambiar de color o de senos); si el
    // hermano no tiene esa combinacion, la medida a secas, como siempre.
    const currentSignature = getCurrentSelectedSignature();
    if (currentSignature && siblingMap[currentSignature]) return siblingMap[currentSignature];

    const currentSize = getCurrentSelectedSize();
    if (!currentSize) return null;

    const exactKey = normalizeSizeKey(currentSize);
    if (siblingMap[exactKey]) return siblingMap[exactKey];

    const numericSize = extractNumericSizeToken(currentSize);
    if (!numericSize) return null;

    const matchedEntry = Object.entries(siblingMap).find(([sizeKey]) => {
      return extractNumericSizeToken(sizeKey) === numericSize;
    });

    return matchedEntry ? matchedEntry[1] : null;
  }

  function buildTargetProductUrl(url) {
    const targetUrl = new URL(url, window.location.href);
    const variantId = resolveTargetVariantId(targetUrl.toString());

    if (variantId) {
      targetUrl.searchParams.set('variant', variantId);
    } else {
      targetUrl.searchParams.delete('variant');
    }

    return targetUrl.toString();
  }

  function syncBlockAttributes(targetBlock, sourceBlock) {
    if (!targetBlock || !sourceBlock) return;

    Array.from(targetBlock.attributes).forEach((attribute) => {
      sourceBlock.removeAttribute(attribute.name);
    });

    Array.from(sourceBlock.attributes).forEach((attribute) => {
      targetBlock.removeAttribute(attribute.name);
    });

    Array.from(targetBlock.attributes).forEach((attribute) => {
      sourceBlock.setAttribute(attribute.name, attribute.value);
    });
  }

  function getMediaGallerySignature(rootNode) {
    const gallery = rootNode?.querySelector('media-gallery');
    if (!gallery) return '';

    return Array.from(gallery.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'))
      .map((item) => item.dataset.mediaId || '')
      .filter(Boolean)
      .join(',');
  }

  function capturePersistentMediaGalleryState() {
    const galleryNode = document.querySelector('media-gallery');
    const instance = galleryNode || null;

    if (!galleryNode) {
      persistentMediaGalleryState = null;
      return null;
    }

    persistentMediaGalleryState = {
      galleryNode,
      instance,
      signature: getMediaGallerySignature(document)
    };

    return persistentMediaGalleryState;
  }

  function restorePersistentMediaGallery(newNode) {
    const preserved = persistentMediaGalleryState;
    const incomingGallery = newNode?.querySelector('media-gallery');
    if (!preserved?.galleryNode || !incomingGallery) return false;

    const incomingIsServerFiltered = incomingGallery.matches?.('media-gallery[data-bl-server-gallery-filter="true"]');
    const preservedIsServerFiltered = preserved.galleryNode.matches?.('media-gallery[data-bl-server-gallery-filter="true"]');
    if (incomingIsServerFiltered || preservedIsServerFiltered) {
      return false;
    }

    const currentSignature = preserved.signature;
    const incomingSignature = getMediaGallerySignature(newNode);

    if (!currentSignature || !incomingSignature || currentSignature !== incomingSignature) {
      return false;
    }

    syncBlockAttributes(incomingGallery, preserved.galleryNode);
    incomingGallery.replaceWith(preserved.galleryNode);
    return true;
  }

  function capturePersistentWashbasinBlockState() {
    const blockNode = document.querySelector('.product__block--BL_washbasin_selector_v2');
    const container = blockNode?.querySelector('[data-washbasin-selector-v2]');
    const slider = container?.querySelector('[data-washbasin-slider]');

    if (!blockNode || !container) {
      persistentWashbasinBlockState = null;
      return null;
    }

    persistentWashbasinBlockState = {
      blockNode,
      container,
      instance: container.__blWbInstance || null,
      selectedValue: container.__blWbInstance?.getSelectedValue?.() || null,
      scrollLeft: slider ? slider.scrollLeft : 0
    };

    return persistentWashbasinBlockState;
  }

  function getCurrentTolstoyBlock() {
    return document.querySelector('tolstoy-stories')?.closest('.product__block') || null;
  }

  function capturePersistentTolstoyBlockState() {
    const blockNode = getCurrentTolstoyBlock();
    const tolstoyNode = blockNode?.querySelector('tolstoy-stories') || null;

    if (!blockNode || !tolstoyNode) {
      persistentTolstoyBlockState = null;
      return null;
    }

    persistentTolstoyBlockState = {
      blockNode,
      tolstoyNode
    };

    return persistentTolstoyBlockState;
  }

  function restorePersistentTolstoyBlock(newNode) {
    const preserved = persistentTolstoyBlockState;
    const incomingBlock = newNode?.querySelector('tolstoy-stories')?.closest('.product__block');
    if (!preserved?.blockNode || !preserved?.tolstoyNode || !incomingBlock) return;

    syncBlockAttributes(incomingBlock, preserved.blockNode);
    incomingBlock.replaceWith(preserved.blockNode);
  }

  function restorePersistentWashbasinBlock(newNode) {
    const preserved = persistentWashbasinBlockState;
    if (!preserved?.blockNode) return;

    const incomingBlock = newNode?.querySelector('.product__block--BL_washbasin_selector_v2');
    if (!incomingBlock) return;

    // Extract the NEW product's config from the incoming section before we discard it
    const incomingContainer = incomingBlock.querySelector('[data-washbasin-selector-v2]');
    const incomingConfigEl = incomingContainer?.querySelector('[data-washbasin-config]');
    let incomingConfig = null;
    if (incomingConfigEl) {
      try { incomingConfig = JSON.parse(incomingConfigEl.textContent); } catch (e) {}
    }

    // Replace the incoming block with our preserved (live) block
    syncBlockAttributes(incomingBlock, preserved.blockNode);
    incomingBlock.replaceWith(preserved.blockNode);

    // Update the surviving instance's config with the NEW product's variant data
    if (preserved.instance && incomingConfig) {
      if (incomingConfig.variantPrices) preserved.instance.config.variantPrices = incomingConfig.variantPrices;
      if (incomingConfig.variantWidths) preserved.instance.config.variantWidths = incomingConfig.variantWidths;
      if (incomingConfig.productId) preserved.instance.config.productId = incomingConfig.productId;
      if (incomingConfig.sectionId) preserved.instance.config.sectionId = incomingConfig.sectionId;
      if (incomingConfig.currentWidth) preserved.instance.config.currentWidth = incomingConfig.currentWidth;

      // Also update the JSON script inside the preserved block so future reads are correct
      const preservedConfigEl = preserved.container.querySelector('[data-washbasin-config]');
      if (preservedConfigEl && incomingConfigEl) {
        preservedConfigEl.textContent = incomingConfigEl.textContent;
      }
    }

    // Re-bind to the new variant picker and update width — silently to avoid visual jumps
    if (preserved.instance) {
      const selectedValue = preserved.selectedValue || preserved.instance.getSelectedValue?.() || 'none';
      const preservedScrollLeft = typeof preserved.scrollLeft === 'number' ? preserved.scrollLeft : 0;

      // Suspend auto-centering briefly so post-swap sync handlers can update
      // visibility/price without recentering the slider. User clicks should
      // still be able to center immediately after the swap.
      preserved.instance._suspendSyncUntil = Date.now() + 900;
      preserved.instance._skipGroupingSwapped = true;

      preserved.instance.refreshVariantBindings();
      preserved.instance.currentMainVariantId = preserved.instance.getCurrentMainVariantId();
      // Use silent width update: detect new width and update state without scroll animation
      const newWidth = preserved.instance.detectWidthFromDOM()
        || preserved.instance.extractWidthFromVariant(preserved.instance.getSelectedMainVariant());
      if (newWidth) {
        preserved.instance.currentWidth = newWidth;
        // Update item visibility for the new width without re-selecting (no scroll jump)
        preserved.instance.items.forEach(item => {
          if (item.hasAttribute('data-washbasin-none')) return;
          const match = preserved.instance.getMatchForWidth(item, newWidth);
          item.style.display = match.variantId ? '' : 'none';
          const pill = preserved.instance.container.querySelector(`[data-pill-value="${item.dataset.washbasinId}"]`);
          if (pill) pill.style.display = match.variantId ? '' : 'none';
          if (match.variantId) {
            const pDisp = item.querySelector('[data-washbasin-price-display]');
            if (pDisp) { pDisp.textContent = `+${preserved.instance.formatMoney(match.price)}`; pDisp.style.display = ''; }
          }
        });
      }

      // Restore the previous explicit basin choice when still valid.
      if (selectedValue) {
        preserved.instance.ensureValidSelection(selectedValue, true);
      }

      // Preserve the exact horizontal viewport instead of recentering.
      // Temporarily suspend CSS scroll-snap-type so the direct scrollLeft
      // assignment is not corrected by the browser to the nearest snap point
      // (snap points may shift when items are shown/hidden for the new width).
      const restoreSliderPosition = () => {
        const sl = preserved.instance.slider;
        if (!sl) return;
        // Disable snap, set position, re-enable snap in the NEXT rAF so the
        // position is committed to layout before snap re-engages.  On that
        // re-engagement the browser will find the set position already IS a
        // valid snap point (we chose it from a previously-snapped state) and
        // make no further adjustment.
        sl.style.scrollSnapType = 'none';
        sl.scrollLeft = preservedScrollLeft;
        requestAnimationFrame(() => {
          if (sl.style.scrollSnapType === 'none') sl.style.scrollSnapType = '';
        });
        preserved.instance.updateProgressBar?.();
      };

      requestAnimationFrame(() => {
        restoreSliderPosition();
        requestAnimationFrame(restoreSliderPosition);
      });

      // Update price without triggering scroll
      preserved.instance.scheduleGlobalPriceUpdate();
    }
  }

  function getCurrentWashbasinContainer() {
    return document.querySelector('[data-washbasin-selector-v2]');
  }

  function getCurrentWashbasinConfig(container = getCurrentWashbasinContainer()) {
    const configNode = container?.querySelector('[data-washbasin-config]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function getSelectedWashbasinItem(container = getCurrentWashbasinContainer()) {
    return container?.querySelector('.washbasin-selector__item.is-active:not([data-washbasin-none])')
      || null;
  }

  function getVariantSizeFromVariant(variant) {
    if (!variant?.options?.length) return null;
    return variant.options.find(looksLikeSizeValue) || null;
  }

  function getWashbasinMatchForWidth(item, targetWidth) {
    if (!item || !targetWidth) return { variantId: null, price: 0, compareAtPrice: 0, regularPrice: 0 };

    let mapping = {};
    let prices = {};
    let variants = {};

    try {
      mapping = JSON.parse(item.dataset.mapping || '{}');
      prices = JSON.parse(item.dataset.prices || '{}');
      variants = JSON.parse(item.dataset.variantData || '{}');
    } catch (error) {
      return { variantId: null, price: 0, compareAtPrice: 0, regularPrice: 0 };
    }

    let variantId = mapping[targetWidth];
    let price = prices[targetWidth];

    if (!variantId) {
      const cleanTarget = targetWidth.toLowerCase().replace(/[^a-z0-9]/g, '');
      const targetNum = extractNumericSizeToken(targetWidth);

      for (const key of Object.keys(mapping)) {
        const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        const keyNum = extractNumericSizeToken(key);
        if (cleanKey === cleanTarget || (targetNum && keyNum === targetNum)) {
          variantId = mapping[key];
          price = prices[key];
          break;
        }
      }
    }

    const variantData = variants[String(variantId)] || {};
    const normalizedPrice = Number.parseInt(price || variantData.priceCents || 0, 10) || 0;
    const compareAtPrice = Number.parseInt(variantData.compareAtPriceCents || 0, 10) || 0;
    return {
      variantId: variantId || null,
      price: normalizedPrice,
      compareAtPrice,
      regularPrice: compareAtPrice > normalizedPrice ? compareAtPrice : normalizedPrice
    };
  }

  function formatMoney(cents) {
    return window.BLBundlePriceState?.formatMoney(cents)
      ?? (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).replace(',00', '');
  }

  function buildBundlePriceState(basePrice, addonPrice, opts = {}) {
    const { owner = 'washbasin-v2' } = opts;
    const state = window.BLBundlePriceState?.buildPriceState(basePrice, addonPrice, opts);
    if (!state) return { total: 0, formattedTotal: '', displayPrice: '', compareAtTotal: 0, compareAtPrice: '', isOnSale: false, owner };
    return { ...state, owner };
  }

  function normalizeComparableValue(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function isSameSizeValue(a, b) {
    const normalizedA = normalizeComparableValue(a);
    const normalizedB = normalizeComparableValue(b);

    if (!normalizedA || !normalizedB) return false;
    if (normalizedA === normalizedB) return true;

    const numericA = extractNumericSizeToken(a);
    const numericB = extractNumericSizeToken(b);
    return numericA.length >= 2 && numericA === numericB;
  }

  function ensurePriceLockCSS() {
    window.BLBundlePriceState?.ensureCSS?.();
  }

  function injectBundlePriceIntoNode(sectionNode, sectionId, bundlePriceState) {
    if (!sectionNode || !bundlePriceState) return;

    ensurePriceLockCSS();

    const priceBlock = sectionNode.querySelector(`#price-${sectionId}`) || sectionNode.querySelector('[id^="price-"]');
    if (!priceBlock) return;

    window.BLBundlePriceState?.applyPriceNode?.(priceBlock, bundlePriceState, { lock: true });
  }

  function buildIncomingBundleState(nextMainSection) {
    const container = getCurrentWashbasinContainer();
    const selectedBasinItem = getSelectedWashbasinItem(container);
    if (!container || !selectedBasinItem) return null;

    const nextVariant = getSelectedVariantFromSection(nextMainSection);
    if (!nextVariant?.id) return null;

    const config = getCurrentWashbasinConfig(container);

    const targetWidth =
      getVariantSizeFromVariant(nextVariant)
      || config?.variantWidths?.[String(nextVariant.id)]
      || getCurrentSelectedSize();

    if (!targetWidth) return null;

    const basinMatch = getWashbasinMatchForWidth(selectedBasinItem, targetWidth);
    if (!basinMatch.variantId || !basinMatch.price) return null;

    const basePrice = Number.parseInt(nextVariant.price || 0, 10);
    if (!basePrice) return null;

    return buildBundlePriceState(basePrice, basinMatch.price, {
      baseCompareAtPrice: Number.parseInt(nextVariant.compare_at_price || 0, 10),
      addonRegularPrice: basinMatch.regularPrice,
      owner: 'washbasin-v2'
    });
  }

  function applyOptimisticUpdate(btn, nextUrl) {
    const group = btn.closest('.product-form__product-group');
    if (group) {
      group.querySelectorAll('a[data-value]').forEach(el => {
        el.classList.remove('active', 'is-selected');
      });
      btn.classList.add('active', 'is-selected');

      const label = group.querySelector('[data-selected-value]');
      const nextLabel = btn.getAttribute('data-color-name') || btn.getAttribute('data-label') || '';
      if (label) {
        label.textContent = nextLabel;
      }
      group.dataset.blSelectedLabel = nextLabel || getStoredSelectedLabel(group);
    }

    history.pushState({}, '', nextUrl);
  }

  /**
   * Parse the main-section HTML from a prefetched sectionMap and eagerly insert
   * <link rel="preload" as="image"> tags for the first few gallery images.
   *
   * WHY: AJAX swaps deliver section HTML instantly (eagerPrefetch) but the
   * images still need to download.  On the first swap to any variant the
   * browser has to fetch the image from scratch, leaving the gallery blank
   * until the download finishes — that blank → image transition is the
   * visible "flash".  Preloading kicks off the image downloads during the
   * eagerPrefetch / hover phase so they are already in cache by the time the
   * user actually clicks a swatch.
   *
   * Only the first `count` images in the main GalleryViewer are preloaded to
   * avoid excessive bandwidth (thumbnails are excluded because they live
   * outside [id^="GalleryViewer"]). `priority` goes to fetchpriority.
   */
  function preloadSectionGalleryImages(sectionHtml, count = HOVER_IMG_COUNT, priority = '') {
    if (!sectionHtml || typeof DOMParser === 'undefined') return;
    try {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      // Prefer the GalleryViewer (main slider); fall back to the full
      // media-gallery element so the function works even if the ID changes.
      const viewer = doc.querySelector('[id^="GalleryViewer"]') || doc.querySelector('media-gallery');
      if (!viewer) return;

      Array.from(viewer.querySelectorAll('img')).slice(0, count).forEach(img => {
        const srcset = img.getAttribute('srcset') || '';
        const src    = img.getAttribute('src')    || '';
        if (!srcset && !src) return;
        // Skip data-URIs (inline SVG placeholders, spinner GIFs, etc.)
        if (src.startsWith('data:') || (srcset && srcset.startsWith('data:'))) return;

        // Derive a stable dedup key from the first URL in srcset (or src).
        const key = srcset ? srcset.split(',')[0].trim().split(' ')[0] : src;
        if (!key || _preloadedImgKeys.has(key)) return;
        _preloadedImgKeys.add(key);

        const link = document.createElement('link');
        link.rel = 'preload';
        link.as  = 'image';
        if (srcset) {
          // imagesrcset/imagesizes let the browser pick the right resolution
          // using the same source-selection algorithm as a real <img srcset>.
          link.setAttribute('imagesrcset', srcset);
          const sizes = img.getAttribute('sizes');
          if (sizes) link.setAttribute('imagesizes', sizes);
        }
        // href is required for browsers that don't support imagesrcset, and
        // for browsers that use it as the fallback URL.
        if (src && !src.startsWith('data:')) link.href = src;
        if (priority) link.setAttribute('fetchpriority', priority);
        document.head.appendChild(link);
      });
    } catch (_) { /* parsing failed — silently skip preload */ }
  }

  // opts.images = cuantas fotos precargar (HOVER_IMG_COUNT por defecto); opts.priority = fetchpriority
  // de esas fotos; opts.requestPriority = prioridad de la peticion de secciones.
  function prefetchSectionData(url, wrapper, opts = {}) {
    const targets = getSectionTargets(wrapper);
    const sectionIds = getUniqueSectionIds(targets);
    if (!sectionIds.length) return null;

    const requestUrl = buildSectionRequestUrl(url, sectionIds);
    const imgCount = opts.images == null ? HOVER_IMG_COUNT : opts.images;

    return fetchSectionMap(requestUrl, { priority: opts.requestPriority })
      .then((sectionMap) => {
        // Kick off image preloads as soon as the section HTML arrives so
        // the images are in browser cache before the user clicks the swatch.
        // Solo si esta URL aun no ha mandado tantas fotos: asi la seccion cacheada no se vuelve a
        // parsear en cada swap.
        const done = _preloadedCountByUrl.get(requestUrl) || 0;
        if (imgCount > done && sectionMap && targets.main.id && sectionMap[targets.main.id]) {
          _preloadedCountByUrl.set(requestUrl, imgCount);
          preloadSectionGalleryImages(sectionMap[targets.main.id], imgCount, opts.priority || '');
        }
        // y la seccion principal ya parseada para que el clic solo tenga que adoptarla
        schedulePreparse(requestUrl, sectionMap, targets.main.id, targets.main.node);
        return sectionMap;
      })
      .catch(() => null);
  }

  function prefetchTarget(url, wrapper) {
    if (!url || url === '#') return;
    const resolvedUrl = buildTargetProductUrl(url);
    prefetchSectionData(resolvedUrl, wrapper);
  }

  // 2026-10-08 (web vitals): la precarga ansiosa arrancaba en DOMContentLoaded y disparaba 12
  // secciones + 36 fotos (~2,4 MB, el 57 % del peso de la ficha) mientras la foto principal aun se
  // descargaba (en campo esa foto empieza a pedirse a ~1,3 s). Ahora espera a que la foto principal
  // haya cargado (tope 4 s) y a un hueco libre del hilo; tras un swap vuelve a programarse fuera de
  // la tarea del clic. El hover/touch sigue precargando al instante, que es lo que da la agilidad.
  function runWhenIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 800);
  }

  function whenMainImageSettled(fn) {
    const img = document.querySelector('[id^="GalleryViewer"] img') || document.querySelector('media-gallery img');
    if (!img || img.complete) { runWhenIdle(fn); return; }
    let done = false;
    const once = () => { if (done) return; done = true; runWhenIdle(fn); };
    img.addEventListener('load', once, { once: true });
    img.addEventListener('error', once, { once: true });
    setTimeout(once, 4000);
  }

  function eagerPrefetch(wrapper) {
    // una vez por root, como el click y el hover: init() puede correr dos veces sobre el mismo
    if (wrapper.dataset.blGroupingEagerBound === 'true') return;
    wrapper.dataset.blGroupingEagerBound = 'true';
    whenMainImageSettled(() => {
      // hubo un swap entre medias: el init() del root nuevo ya programo el suyo
      if (liveWrapper(wrapper) !== wrapper) return;

      const seen = new Set();
      const eagerOpts = { images: EAGER_IMG_COUNT, priority: 'low' };
      const links = Array.from(wrapper.querySelectorAll('a[data-value]'));
      const reachedGroups = new Set();
      const firstBatch = [];

      links.forEach((link, index) => {
        if (index >= EAGER_PREFETCH_LIMIT) return;
        reachedGroups.add(link.closest('.product-form__product-group'));

        const href = link.getAttribute('href');
        if (!href || href === '#') return;

        const resolvedUrl = buildTargetProductUrl(href);
        if (seen.has(resolvedUrl)) return;

        seen.add(resolvedUrl);
        firstBatch.push(prefetchSectionData(resolvedUrl, wrapper, eagerOpts));
      });

      // V4: las medidas PROPIAS son el control mas pulsado -> tope propio. Las ajenas solo por hover.
      let count = 0;
      wrapper.querySelectorAll('[data-bl-size][data-bl-size-own]:not(.is-selected)').forEach((pill) => {
        if (count >= EAGER_SIZE_PREFETCH_LIMIT) return;
        const u = sizePillUrl(pill);
        if (!u || seen.has(u)) return;
        seen.add(u); count++;
        firstBatch.push(prefetchSectionData(u, wrapper, eagerOpts));
      });

      Promise.allSettled(firstBatch).then(() => runWhenIdle(() => eagerPrefetchExtra(wrapper, links, reachedGroups, seen)));
    });
  }

  // 2026-10-15: segunda tanda, cuando la primera ya ha llegado y a prioridad baja (peticion y foto).
  // (1) Filas a las que el tope de 12 no llega: en Ronda los 15 colores van primero y Griff y
  // Konfiguration solo se precargaban con hover; el salto por Konfiguration tardaba ~1 s (0,9-1,04 s
  // en develop) si el cliente no pasaba antes el raton. Se salta lo ya elegido y el resto de una fila
  // que la primera tanda ya corto (los colores 13-15). Tope 4, 1 foto por hermano.
  // (2) La URL a la que lleva Atras, si no esta en cache: la pagina de entrada nunca se pidio por
  // secciones y volver a ella costaba una peticion de ~0,9 s. Sin fotos (ya se vieron).
  function eagerPrefetchExtra(wrapper, links, reachedGroups, seen) {
    if (liveWrapper(wrapper) !== wrapper) return;

    const extraOpts = { images: EAGER_EXTRA_IMG_COUNT, priority: 'low', requestPriority: 'low' };
    let count = 0;
    links.forEach((link) => {
      if (count >= EAGER_EXTRA_PREFETCH_LIMIT) return;
      if (reachedGroups.has(link.closest('.product-form__product-group'))) return;
      if (link.classList.contains('active') || link.classList.contains('is-selected')) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      const resolvedUrl = buildTargetProductUrl(href);
      if (seen.has(resolvedUrl) || new URL(resolvedUrl).pathname === window.location.pathname) return;

      seen.add(resolvedUrl); count++;
      prefetchSectionData(resolvedUrl, wrapper, extraOpts);
    });

    if (backUrl && backUrl !== window.location.href && !seen.has(backUrl)) {
      seen.add(backUrl);
      prefetchSectionData(backUrl, wrapper, { images: 0, requestPriority: 'low' });
    }
  }

  /* ── Hover: label updates + speculation rules ── */
  function bindHoverHandlers(wrapper) {
    wrapper.querySelectorAll('.product-form__product-group').forEach(group => {
      getStoredSelectedLabel(group);
    });

    wrapper.querySelectorAll('.swatch-color, .bl-option-btn').forEach(swatch => {
      if (swatch.hasAttribute('data-bl-size')) return;   // V4: la fila de medida tiene su propio hover (abajo)
      if (swatch.dataset.blGroupingHoverBound === 'true') return;
      swatch.dataset.blGroupingHoverBound = 'true';

      const group = swatch.closest('.product-form__product-group');
      const label = group ? group.querySelector('[data-selected-value]') : null;
      const original = label ? getStoredSelectedLabel(group, label.textContent) : '';

      swatch.addEventListener('pointerenter', () => {
        if (label) {
          label.textContent = swatch.getAttribute('data-color-name') || swatch.getAttribute('data-label') || '';
        }
        prefetchTarget(swatch.getAttribute('href'), wrapper);
      });

      swatch.addEventListener('mouseleave', () => {
        if (label) label.textContent = getStoredSelectedLabel(group, original);
      });

      swatch.addEventListener('touchstart', () => {
        prefetchTarget(swatch.getAttribute('href'), wrapper);
      }, { passive: true });
    });

    // V4 (2026-09-11): hover de la fila de MEDIDA. Dos bugs del hover generico que esto evita:
    // (1) las pills no llevaban data-color-name -> la etiqueta se vaciaba al pasar por encima y al
    // salir volvia al valor CACHEADO al cargar (60), aunque se hubiera pulsado otra medida;
    // (2) prefetchTarget(href) pasa por buildTargetProductUrl, que busca la medida ACTUAL en el
    // destino y la borra: precargaba la URL equivocada. Aqui la etiqueta es la medida y la URL
    // es la exacta (?variant= del pill), sin prerender (speculate) porque nunca navegamos entero.
    wrapper.querySelectorAll('[data-bl-size]').forEach((pill) => {
      if (pill.dataset.blGroupingHoverBound === 'true') return;
      pill.dataset.blGroupingHoverBound = 'true';
      const group = pill.closest('.product-form__product-group');
      const label = group ? group.querySelector('[data-bl-size-selected]') : null;
      pill.addEventListener('pointerenter', () => {
        if (label) label.textContent = pill.getAttribute('data-bl-size') || '';
        const u = sizePillUrl(pill);
        if (u) prefetchSectionData(u, wrapper);
      });
      pill.addEventListener('mouseleave', () => {
        if (label && group) label.textContent = getStoredSelectedLabel(group, label.textContent);
      });
      pill.addEventListener('touchstart', () => {
        const u = sizePillUrl(pill);
        if (u) prefetchSectionData(u, wrapper);
      }, { passive: true });
    });
  }

  // 2026-10-08: fuera las Speculation Rules (prerender de la ficha completa de cada hermano, 12 por
  // ficha): el cambio de color es un swap AJAX y nunca navega entero, Chrome no llego a crear ni un
  // prerender en las pruebas, y en navegadores sin soporte caian a <link rel=prefetch> del HTML
  // completo (~115 KB gz por hermano) que tampoco se usaba.

  /* ── Click handler (event delegation) ── */
  /* ── V4 (2026-09-11): fila de MEDIDA de la linea (snippets/BL-grouping-size-row.liquid) ── */
  function markSizePill(wrapper, btn) {
    wrapper.querySelectorAll('[data-bl-size]').forEach((n) => {
      const on = n === btn;
      n.classList.toggle('is-selected', on);
      n.classList.toggle('active', on);
      if (n.hasAttribute('aria-pressed')) n.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const label = wrapper.querySelector('[data-bl-size-selected]');
    const size = btn.getAttribute('data-bl-size') || '';
    if (label) label.textContent = size;
    const group = btn.closest('.product-form__product-group');
    if (group) group.dataset.blSelectedLabel = size;   // el hover restaura desde aqui al salir
  }
  // URL exacta del pill (producto + ?variant=), la misma cadena en hover y en click para que la
  // cache de secciones (fetchSectionMap) acierte.
  function sizePillUrl(pill) {
    const url = pill.getAttribute('data-bl-size-url');
    if (!url) return null;
    const target = new URL(url, window.location.href);
    const vid = sizeSignatureVariant(target.pathname, pill.getAttribute('data-bl-size')) || pill.getAttribute('data-bl-size-variant');
    if (vid) target.searchParams.set('variant', vid);
    return target.toString();
  }
  // 2026-09-23 (lavabos de pared): con varias opciones nativas (Breite x Schürze x Hahnloch) la variante
  // del markup es la de cuando se pinto la pagina; si el cliente cambio el Hahnloch despues, la medida
  // lo perderia. Se busca en la matriz del grouping la firma "<medida>|<resto de la firma actual>".
  // Con una sola opcion (muebles) no hay firma y manda el markup, como siempre.
  function sizeSignatureVariant(path, size) {
    const signature = getCurrentSelectedSignature();
    if (!signature || !size) return null;
    const siblingMap = (window.__blGroupingMatrix || {})[path];
    if (!siblingMap) return null;
    const rest = signature.slice(signature.indexOf('|') + 1);
    const want = String(size);
    const key = Object.keys(siblingMap).find((k) => {
      const cut = k.indexOf('|');
      return cut > 0 && k.slice(cut + 1) === rest && extractNumericSizeToken(k.slice(0, cut)) === want;
    });
    return key ? siblingMap[key] : null;
  }
  // Medida PROPIA: mueve el control nativo oculto (select o radio) y dispara `change`, que es lo que
  // escucha variant-selects; el tema resuelve variante, precio, galeria, ATC y URL como siempre, y
  // el selector v3 recibe su variant:changed. Devuelve false si no hay control nativo con esa medida.
  function selectNativeSize(wrapper, size) {
    const scope = wrapper.closest('.shopify-section') || document;
    const picker = scope.querySelector('variant-selects, variant-radios');
    if (!picker) return false;
    const want = String(size);
    const matches = (v) => { const t = (v || '').trim(); return t === want || extractNumericSizeToken(t.split(/\s+/)[0]) === want; };
    for (const sel of picker.querySelectorAll('select')) {
      const opt = Array.from(sel.options).find((o) => matches(o.value));
      if (!opt) continue;
      if (sel.value !== opt.value) { sel.value = opt.value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
      return true;
    }
    for (const radio of picker.querySelectorAll('input[type="radio"]')) {
      if (!matches(radio.value)) continue;
      if (!radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      return true;
    }
    return false;
  }

  function bindClickHandler(wrapper) {
    if (wrapper.dataset.blGroupingClickBound === 'true') return;
    wrapper.dataset.blGroupingClickBound = 'true';

    wrapper.addEventListener('click', (e) => {
      // V4: medida de la linea. Propia Y ajena van por el MISMO swap AJAX del grouping, con el
      // ?variant= ya resuelto por Liquid y precargado en hover/idle (11-09: el cambio de variante
      // nativo tardaba ~1 s en repintar la galeria). No pasa por buildTargetProductUrl a proposito:
      // ese busca la medida ACTUAL en el destino y la borraria. Sin URL en el markup (no deberia
      // pasar) cae al control nativo oculto.
      const sizeBtn = e.target.closest('[data-bl-size]');
      if (sizeBtn) {
        e.preventDefault();
        if (sizeBtn.hasAttribute('data-bl-size-own') && sizeBtn.classList.contains('is-selected')) return;
        const swapUrl = sizePillUrl(sizeBtn);
        if (!swapUrl) {
          if (sizeBtn.hasAttribute('data-bl-size-own') && selectNativeSize(wrapper, sizeBtn.getAttribute('data-bl-size'))) markSizePill(wrapper, sizeBtn);
          return;
        }
        markSizePill(wrapper, sizeBtn);
        backUrl = window.location.href;
        try { history.pushState({}, '', swapUrl); } catch (err) {}
        // INP: primero se pinta la pill marcada, el swap va en la tarea siguiente (ver afterNextPaint)
        afterNextPaint(() => navigateAjax(swapUrl, liveWrapper(wrapper), { historyAlreadyUpdated: true }));
        return;
      }

      const btn = e.target.closest('a[data-value]');
      if (!btn) return;

      const href = btn.getAttribute('href');
      if (!href || href === '#') return;

      const resolvedUrl = buildTargetProductUrl(href);
      const targetUrl = new URL(resolvedUrl, window.location.href);
      const currentUrl = new URL(window.location.href);
      targetUrl.hash = '';
      currentUrl.hash = '';

      if (targetUrl.toString() === currentUrl.toString()) return;

      e.preventDefault();

      backUrl = window.location.href;
      applyOptimisticUpdate(btn, resolvedUrl);
      // INP: primero se pinta el swatch marcado, el swap va en la tarea siguiente (ver afterNextPaint)
      afterNextPaint(() => navigateAjax(resolvedUrl, liveWrapper(wrapper), { historyAlreadyUpdated: true }));
    });
  }

  /* ── Pre-swap cleanup: preserve persistent state, destroy only what won't survive ── */
  function destroyAddonInstances() {
    capturePersistentWashbasinBlockState();
    capturePersistentMediaGalleryState();
    capturePersistentTolstoyBlockState();

    // Only destroy washbasin instances that are NOT being preserved
    document.querySelectorAll('[data-washbasin-selector-v2][data-initialized]').forEach(el => {
      if (persistentWashbasinBlockState?.container === el) return;
      if (el.__blWbInstance && typeof el.__blWbInstance.destroy === 'function') {
        el.__blWbInstance.destroy();
      }
    });
    // Preserve washbasin price state through AJAX swaps to avoid a visible
    // flash of the native price before the new selector instance rebuilds it.
    document.body.classList.remove('wb-btn-ready');
  }

  function prepareIncomingMediaGallery(newNode, preSwapGalleryHeight, preSwapMediaGalleryHeight) {
    const mediaGallery = newNode?.querySelector('media-gallery');
    if (!mediaGallery) return;

    mediaGallery.dataset.blDeferInit = 'true';

    if (!mediaGallery.matches?.('media-gallery[data-bl-server-gallery-filter="true"]')) return;

    // For desktop grid layouts (enableDesktopSlider=false), Swiper never initialises,
    // so the revealWhenStable loop in postProcessSectionSwap would hold opacity:0 for
    // ~400ms before timing out.  The gallery content is ordered synchronously before
    // the first paint, so no wrong-order flash occurs without pre-masking.
    const isDesktopGrid = mediaGallery.dataset.enableDesktopSlider !== 'true'
      && !window.FoxTheme?.config?.mqlMobile;
    if (isDesktopGrid) return;

    mediaGallery.style.opacity = '0';
    mediaGallery.style.pointerEvents = 'none';
    if (preSwapMediaGalleryHeight > 0) {
      mediaGallery.style.minHeight = preSwapMediaGalleryHeight + 'px';
    }

    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (viewer && preSwapGalleryHeight > 0) {
      viewer.style.minHeight = preSwapGalleryHeight + 'px';
    }

    const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails) {
      thumbnails.style.opacity = '0';
      thumbnails.style.pointerEvents = 'none';
    }
  }

  function postProcessSectionSwap(newNode, url, mainSectionId, preSwapGalleryHeight, preSwapMediaGalleryHeight) {
    restorePersistentWashbasinBlock(newNode);
    restorePersistentTolstoyBlock(newNode);

    const variant = getSelectedVariantFromSection(newNode);
    const variantHasFeaturedMedia = Boolean(variant?.featured_media?.id || variant?.featured_image?.id);
    const hasServerFilteredGallery = Boolean(newNode.querySelector('media-gallery[data-bl-server-gallery-filter="true"]'));
    const reusedPersistentMediaGallery =
      !hasServerFilteredGallery
      && variantHasFeaturedMedia
      && restorePersistentMediaGallery(newNode);

    const mediaGallery = newNode.querySelector('media-gallery');
    if (mediaGallery && reusedPersistentMediaGallery && variant && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.removeAttribute('data-bl-defer-init');
      mediaGallery.setActiveMedia(variant);
      mediaGallery.sliderInstance?.slider?.update?.();
      mediaGallery.sliderInstance?.slider?.updateAutoHeight?.(0);
    } else if (mediaGallery && typeof mediaGallery.init === 'function') {
      // ── Compute the correct starting slide from the incoming variant so we
      //    can pass it to Swiper as `initialSlide` rather than letting Swiper
      //    render slide 0 and then immediately calling slideTo(N).  The latter
      //    produces a 1-frame flash on the first swap (Swiper's autoHeight
      //    callback can fire asynchronously even when speed = 0).
      const _initialSlide = (() => {
        if (!variant) return 0;
        const fm = variant.featured_media ||
          (variant.featured_image
            ? { position: variant.featured_image.position || 1 }
            : null);
        return fm ? Math.max(0, (fm.position || 1) - 1) : 0;
      })();

      // Temporarily override setSliderOptions so that, the moment it rebuilds
      // sliderOptions inside init(), we can inject `initialSlide` before
      // FoxTheme.Carousel/Swiper is constructed.  The override deletes itself
      // after the first call, restoring the prototype method.
      if (!hasServerFilteredGallery && _initialSlide > 0) {
        mediaGallery.setSliderOptions = function _patchedSetSliderOptions() {
          delete this.setSliderOptions; // restore prototype method immediately
          this.setSliderOptions();      // call original (now via prototype)
          this.sliderOptions.initialSlide = _initialSlide;
        };
      }

      // ── Apply a min-height on the incoming GalleryViewer equal to the
      //    outgoing viewer height.  This prevents the layout from collapsing
      //    to 0 while Swiper's autoHeight reads 0 for unloaded images.
      //
      //    IMPORTANT: clearMinHeight must call updateAutoHeight(0) BEFORE
      //    removing min-height.  If min-height is removed first, there is a
      //    window where Swiper still has height:0 and min-height is gone →
      //    the viewer collapses → that collapse is the visible "flash".
      //    Calling updateAutoHeight(0) forces Swiper to re-measure the now-
      //    loaded image synchronously, setting the correct height, so the
      //    min-height removal is a no-op from a layout perspective.
      const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      if (viewer && preSwapGalleryHeight > 0) {
        viewer.style.minHeight = preSwapGalleryHeight + 'px';

        const clearMinHeight = () => {
          // Force Swiper to adopt the loaded image height before we lift the
          // min-height guard; without this the viewer momentarily collapses.
          const sl = mediaGallery.sliderInstance?.slider;
          if (sl && typeof sl.updateAutoHeight === 'function') {
            sl.updateAutoHeight(0);
          }
          viewer.style.minHeight = '';
        };

        const firstImg = viewer.querySelector('img');
        if (firstImg?.complete && firstImg.naturalHeight > 0) {
          clearMinHeight();
        } else if (firstImg) {
          firstImg.addEventListener('load', clearMinHeight, { once: true });
          firstImg.addEventListener('error', clearMinHeight, { once: true });
          // Safety net: clear regardless after 1.5 s so nothing stays locked
          setTimeout(clearMinHeight, 1500);
        } else {
          requestAnimationFrame(clearMinHeight);
        }
      }

      const releaseGalleryMask = () => {
        mediaGallery.style.opacity = '';
        mediaGallery.style.pointerEvents = '';
        mediaGallery.style.minHeight = '';
        thumbnails?.style.removeProperty('opacity');
        thumbnails?.style.removeProperty('pointer-events');
      };

      const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');

      // Desktop grid layouts have no Swiper — skip the opacity masking and
      // revealWhenStable machinery that wait for swiper-initialized (which never fires).
      const isDesktopGrid = hasServerFilteredGallery
        && mediaGallery.dataset.enableDesktopSlider !== 'true'
        && !window.FoxTheme?.config?.mqlMobile;

      if (hasServerFilteredGallery && !isDesktopGrid) {
        mediaGallery.style.opacity = '0';
        mediaGallery.style.pointerEvents = 'none';
        if (preSwapMediaGalleryHeight > 0) {
          mediaGallery.style.minHeight = preSwapMediaGalleryHeight + 'px';
        }
        if (thumbnails) {
          thumbnails.style.opacity = '0';
          thumbnails.style.pointerEvents = 'none';
        }
      }

      mediaGallery.removeAttribute('data-bl-defer-init');
      if (mediaGallery.initialized !== true) {
        mediaGallery.init();
      } else {
        mediaGallery.sliderInstance?.slider?.update?.();
        mediaGallery.sliderInstance?.slider?.updateAutoHeight?.(0);
      }

      if (hasServerFilteredGallery) {
        if (isDesktopGrid) {
          // Grid layout: no Swiper, no masking — clear any opacity set by
          // prepareIncomingMediaGallery on browsers that ran it before the guard.
          releaseGalleryMask();
        } else {
          const firstImg = viewer?.querySelector('img');
          let rafAttempts = 0;
          const maxRafAttempts = 24;

          const isImageReady = () => {
            return !firstImg || (firstImg.complete && firstImg.naturalHeight > 0);
          };

          const isGalleryReady = () => {
            // For grid layouts (sliderInstance remains false after init),
            // swiper-initialized never fires — treat as ready once image loads.
            const swiperActive = mediaGallery.sliderInstance !== false;
            const viewerReady = !viewer || !swiperActive || viewer.classList.contains('swiper-initialized');
            const thumbsReady = !thumbnails || !swiperActive || thumbnails.classList.contains('swiper-initialized');
            return viewerReady && thumbsReady && isImageReady();
          };

          const revealWhenStable = () => {
            rafAttempts += 1;
            if (isGalleryReady() || rafAttempts >= maxRafAttempts) {
              if (viewer?.style.minHeight) {
                const sl = mediaGallery.sliderInstance?.slider;
                if (sl && typeof sl.updateAutoHeight === 'function') {
                  sl.updateAutoHeight(0);
                }
                viewer.style.minHeight = '';
              }
              releaseGalleryMask();
              return;
            }
            requestAnimationFrame(revealWhenStable);
          };

          requestAnimationFrame(revealWhenStable);
          setTimeout(() => {
            if (viewer?.style.minHeight) viewer.style.minHeight = '';
            releaseGalleryMask();
          }, 1200);
        }
      }
    }

    if (mediaGallery && !hasServerFilteredGallery && variant && typeof mediaGallery.setActiveMedia === 'function') {
      // setActiveMedia is still called as a safety net (e.g. when featured_media
      // has no position, or for the desktop-stack / non-slider layout path that
      // uses sortMediaItems instead of slideTo).  When initialSlide was already
      // set correctly above, slideTo() is a fast no-op.
      mediaGallery.setActiveMedia(variant);
    }

    window?.Shopify?.PaymentButton?.init();
    window?.ProductModel?.loadShopifyXR();

    if (window.blVariantSeoData) {
      const newVariantInput = newNode.querySelector('input[name="id"]');
      if (newVariantInput && window.blVariantSeoData[newVariantInput.value]) {
        document.querySelectorAll('.bl-variant-seo-text').forEach(block => {
          block.innerHTML = window.blVariantSeoData[newVariantInput.value];
        });
      }
    }

    if (variant) {
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant }
      }));
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant }
      }));

      // 2026-08-28 (hardening): el `?.()` solo protege si falta `publish`; con publish presente
      // el argumento SÍ se evalúa, así que un PUB_SUB_EVENTS ausente lanzaría AQUÍ e impediría el
      // `bl:grouping:swapped` de abajo — que consumen ambos selectores, ambos carriles de addons,
      // el caption y el puente de Elevar. 'variant-change' es el nombre de
      // cable que define theme.js: si el vendor reestructura pubsub, el canal sigue vivo.
      try {
        const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
        if (pubsub && typeof pubsub.publish === 'function') {
          const pubsubEvents = pubsub.PUB_SUB_EVENTS || {};
          pubsub.publish(pubsubEvents.variantChange || 'variant-change', {
            data: {
              sectionId: mainSectionId,
              html: newNode,
              variant
            }
          });
        }
      } catch (error) {
        // El swap y bl:grouping:swapped deben seguir.
      }
    }

    document.dispatchEvent(new CustomEvent('bl:grouping:swapped', {
      detail: {
        url,
        sectionId: mainSectionId,
        variant
      }
    }));
  }

  /* ── AJAX navigation: fetch + swap section only ── */
  function navigateAjax(url, wrapper, options = {}) {
    const targets = getSectionTargets(wrapper);
    const sectionIds = getUniqueSectionIds(targets);
    const viewportPosition = captureViewportPosition();

    if (!targets.main.node || !targets.main.id || !sectionIds.length) {
      window.location.href = url;
      return;
    }

    // Abort any in-flight request
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const navigationSignal = abortController.signal;

    // Destroy addon instances before swap to prevent stale listeners/state.
    destroyAddonInstances();

    const requestUrl = buildSectionRequestUrl(url, sectionIds);
    const cachedSectionMap = getCached(requestUrl);
    const hasPendingPrefetch = pendingPrefetches.has(requestUrl);
    const shouldShowLoadingState = !cachedSectionMap && !hasPendingPrefetch;

    if (shouldShowLoadingState) {
      applyLoadingState(targets.main.node, true);
      if (targets.sticky.node && targets.sticky.node !== targets.main.node) {
        applyLoadingState(targets.sticky.node, true);
      }
    }

    const sectionRequest = cachedSectionMap
      ? Promise.resolve(cachedSectionMap)
      : fetchSectionMap(requestUrl, { signal: navigationSignal });

    sectionRequest
      .then(sectionMap => {
        if (navigationSignal.aborted) return;

        const nextMainSection = takePreparsed(requestUrl, targets.main.id)
          || parseSectionMarkup(sectionMap[targets.main.id], targets.main.node);
        if (!nextMainSection) throw new Error(`Missing section HTML for ${targets.main.id}`);

        // 2026-07-29: gate de TEMPLATE — el Section Rendering API renderiza la sección del template
        // ACTUAL con el producto destino, así que un swap v2↔v3 pintaría el producto destino con los
        // bloques del template origen (sin su selector propio) y dejaría estado v3 huérfano en window
        // (lavabo "anclado" en /cart/add). Si el template_suffix entrante difiere del vivo → página
        // completa. Si algún lado no trae el atributo (deploy antiguo), no se bloquea nada.
        const currentSuffix = wrapper?.getAttribute?.('data-bl-template-suffix');
        const incomingSuffix = nextMainSection
          .querySelector('[data-bl-grouping-root]')
          ?.getAttribute?.('data-bl-template-suffix');
        if (currentSuffix != null && incomingSuffix != null && currentSuffix !== incomingSuffix) {
          window.location.href = url;
          return;
        }

        const incomingBundleState = buildIncomingBundleState(nextMainSection);
        if (incomingBundleState?.formattedTotal) {
          // Inject into the NEW section DOM so the price is visible immediately
          // after the swap — no gap before washbasin re-initializes.
          injectBundlePriceIntoNode(nextMainSection, targets.main.id, incomingBundleState);
          window.BLBundlePriceState?.setOwnerState?.(
            incomingBundleState.owner || 'washbasin-v2',
            true,
            { buttonReady: true }
          );
          window.BLAtcPriceSync?.applyBundlePrice(incomingBundleState.owner || 'washbasin-v2', incomingBundleState.formattedTotal);
        } else {
          // No basin addon — clear override so native price is visible immediately.
          window.BLBundlePriceState?.setOwnerState?.('washbasin-v2', false);
          window.BLAtcPriceSync?.clearBundlePrice('washbasin-v2');
        }

        updateDocumentTitle(nextMainSection);

        // Capture the current gallery viewer height BEFORE the swap so we can
        // hold the new gallery at the same height until its first image loads.
        // This prevents the Swiper autoHeight flash caused by unloaded images
        // momentarily reporting 0 height on first-swap (images are browser-cached
        // after the first swap, so subsequent swaps never flash).
        const _preSwapGalleryHeight = (() => {
          const viewer = targets.main.node.querySelector('media-gallery [id^="GalleryViewer"]');
          return viewer ? viewer.offsetHeight : 0;
        })();
        const _preSwapMediaGalleryHeight = (() => {
          const gallery = targets.main.node.querySelector('media-gallery');
          return gallery ? gallery.offsetHeight : 0;
        })();

        viewTransitionAdopt(
          targets.main.node,
          nextMainSection,
          [
            (incomingNode) => prepareIncomingMediaGallery(
              incomingNode,
              _preSwapGalleryHeight,
              _preSwapMediaGalleryHeight
            )
          ],
          [
            (newNode) => postProcessSectionSwap(
              newNode,
              url,
              targets.main.id,
              _preSwapGalleryHeight,
              _preSwapMediaGalleryHeight
            )
          ]
        );

        if (
          targets.sticky.node
          && targets.sticky.id
          && targets.sticky.node !== targets.main.node
          && sectionMap[targets.sticky.id]
        ) {
          const nextStickySection = parseSectionMarkup(sectionMap[targets.sticky.id], targets.sticky.node);
          if (nextStickySection) {
            if (incomingBundleState?.formattedTotal) {
              injectBundlePriceIntoNode(nextStickySection, targets.sticky.id, incomingBundleState);
            }
            HTMLUpdateUtility.viewTransition(targets.sticky.node, nextStickySection);
          }
        }

        // Descripcion avanzada, Descargas y cualquier seccion marcada `bl-grouping-refresh` (BL-121).
        (targets.refresh || []).forEach(target => {
          if (
            !target.node
            || !target.id
            || target.node === targets.main.node
            || target.node === targets.sticky.node
            || !Object.prototype.hasOwnProperty.call(sectionMap, target.id)
          ) {
            return;
          }

          const nextRefreshSection = parseOptionalSectionMarkup(sectionMap[target.id], target.node);
          if (nextRefreshSection) {
            const accordionState = captureAccordionState(target.node);
            HTMLUpdateUtility.viewTransition(target.node, nextRefreshSection, [
              (incomingNode) => applyAccordionState(incomingNode, accordionState)
            ]);
          }
        });

        if (!options.historyAlreadyUpdated) {
          history.pushState({}, '', new URL(url, window.location.href).toString());
        }

        init();
        restoreViewportPosition(viewportPosition);
      })
      .catch(err => {
        if (shouldShowLoadingState) {
          applyLoadingState(targets.main.node, false);
          if (targets.sticky.node && targets.sticky.node !== targets.main.node) {
            applyLoadingState(targets.sticky.node, false);
          }
        }

        if (navigationSignal.aborted || err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  /* ── Popstate: back/forward button support ── */
  function bindPopstate() {
    if (window.__blGroupingPopstateBound) return;
    window.__blGroupingPopstateBound = true;

    window.addEventListener('popstate', () => {
      backUrl = null;   // tras Atras/Adelante no se sabe que hay detras en el historial
      navigateAjax(window.location.href, document.querySelector('[data-bl-grouping-root]'), { historyAlreadyUpdated: true });
    });
  }

  window.__blGroupingInit = init;
  document.addEventListener('shopify:section:load', init);

  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
